// services/kontoRows.js — редовете на Excel файла за Конто 6 (чиста функция).
// Доставките вече не минават оттук — от ПРОМТ-29 (27.10.2026) те са TXT за
// „Импорт на записвания" (services/kontoPurchaseRecords.js).
//
// Пренесено от src/invoices/xlsx.js (buildKonto6Xlsx), за да може ДА СЕ
// ТЕСТВА без браузър: test/konto-export-unchanged.test.js сравнява резултата
// клетка по клетка със записан образец от кода преди ПРОМТ-7. Форматът е
// ФИКСИРАН — потребителят внася файла директно в Конто 6 и всяка разместена
// колона или сменен код му чупи вноса. Кодовете идват от
// services/kontoConfig.js (файл + презаписи по фирма), не от константи тук.
//
// Върнатият aoa (array of arrays) е точно това, което XLSX.utils.aoa_to_sheet
// получава в браузъра: ред 1 етикети, ред 2 технически кодове, после данни.

const { round2 } = require('./totals');
const kontoConfig = require('./kontoConfig');
const { isCashPayment } = require('./payment');

const { LABELS, COLS, CI, eom, df } = require('./konto6Format');

// rows: избраните документи; contacts: контактите на фирмата; allInvoices:
// за номера на референтната фактура при КИ/ДИ; company: за презаписите.
// Връща { aoa, skipped, count }.
function buildKonto6Rows(rows, contacts, allInvoices, company, opts) {
  const K = kontoConfig.forCompany(company);
  const o = opts || {};
  // Режимът: изрично подаден, иначе настройката на фирмата. При summary
  // (днешният) — по един ред на документ, байт по байт както преди.
  const lines = (o.mode || (company && company.konto_export_mode)) === 'lines';
  const arts = articleIndex(o.articles);
  const skipped = [];
  const dataRows = [];
  (rows || []).forEach((inv) => {
    const c = (contacts || []).find((x) => x.id === inv.contact_id) || {};
    // „в брой", „Брой"… — не само точното „В брой" (Промени 16.10, т. 3д).
    const isCash = isCashPayment(inv.payment_method);
    const dt = inv.doc_type || 'invoice';
    const payCode = isCash ? K.pay_cash : K.pay_bank;
    // Платената в брой може да има свой код на документ (т. 3д) — същото
    // правило като при избора кои документи влизат (services/kontoExport.js).
    const docCode = kontoConfig.docCode(K, dt, isCash);
    const deal = kontoConfig.dealCode(K, inv.tax_treatment);
    if (!docCode) { skipped.push(inv.number || ('#' + inv.id)); return; }
    let fromDoc = '';
    if (dt === 'credit' || dt === 'debit') {
      fromDoc = inv.ref_invoice_number || '';
      if (!fromDoc && inv.ref_invoice_id && allInvoices) {
        const ref = allInvoices.find((x) => x.id === inv.ref_invoice_id);
        if (ref) fromDoc = ref.number;
      }
    }
    const base = round2(inv.subtotal), vat = round2(inv.vat_amount);
    const row = [];
    for (let i = 0; i < COLS.length; i++) row.push('');
    const S = (code, v) => { row[CI[code]] = v; };
    S('H.DOC_TYPE', docCode); S('H.DOC_NO', inv.number); S('H.DOC_DATE', df(inv.issue_date)); S('H.FROM_DOC', fromDoc);
    S('H.DOC_VALEUR', df(valeurOf(K, inv.issue_date))); S('H.TAX_EVENT_DATE', df(inv.issue_date));
    S('H.PARTNER_TYPE', K.partner_types.customer); S('H.PARTNER', kontoConfig.partnerCode(K, c));
    S('P.PARTNER_NAME', c.name || ''); S('P.PARTNER_ID', c.eik || '');
    S('P.PARTNER_VAT_ID', c.vat_number || c.eik || '');
    S('H.PARTNER_ADDRESS', [c.city, c.address].filter(Boolean).join(', '));
    S('H.PARTNER_MOLL', c.mol || '');
    S('H.PAYMENT_TYPE', payCode); S('H.DUE_DATE', df(inv.due_date || inv.issue_date));
    S('H.VALUE_LOCAL', base); S('H.VAT_LOCAL', vat);
    S('H.CURRENCY', K.currency); S('H.EXCHANGE_RATE', K.rate);
    S('H.VALUE_FOREIGN', base); S('H.VAT_FOREIGN', vat);
    S('H.DESCRIPTION', K.article_name);
    S('D.ROW_NO', 1); S('D.DEAL_TYPE', deal);
    S('D.ARTICLE_TYPE', K.article_type); S('D.ARTICLE_ID', K.article_id);
    S('A.ARTICLE_NAME', K.article_name); S('D.MEASURE', K.measure); S('D.QTY', 1);
    S('D.PRICE_LOCAL', base); S('D.PRICE_FOREIGN', base);
    S('D.PRICE_VAT_LOCAL', vat); S('D.PRICE_VAT_FOREIGN', vat);
    S('D.VALUE_LOCAL', base); S('D.VALUE_FOREIGN', base);
    S('D.VAT_LOCAL', vat); S('D.VAT_FOREIGN', vat);
    // Допълнителните реквизити с постоянна стойност (т. 3г) — само тези, които
    // фирмата е попълнила; празните колони остават празни както досега.
    for (const k of Object.keys(K.fields || {})) {
      if (CI[k] != null && kontoConfig.FIELD_KEYS.includes(k) && !kontoConfig.isEmpty(K.fields[k])) S(k, K.fields[k]);
    }
    if (!lines) { dataRows.push(row); return; }
    // Режим lines (ПРОМТ-11): по един D ред на ред от фактурата. Заглавието
    // (H.*) е същото като при summary и се повтаря на всеки ред на документа.
    for (const d of saleLines(inv, base, vat, K, arts, deal)) {
      const r2 = row.slice();
      for (const k of Object.keys(d)) r2[CI[k]] = d[k];
      dataRows.push(r2);
    }
  });
  return { aoa: [LABELS, COLS].concat(dataRows), skipped, count: dataRows.length };
}

// Вальорът по настройката на фирмата: последният ден на месеца (както
// досега) или самата дата на документа.
function valeurOf(K, iso) {
  return K.valeur === 'doc' ? iso : eom(iso);
}

function articleIndex(list) {
  const m = new Map();
  for (const a of (Array.isArray(list) ? list : [])) if (a && a.id != null) m.set(Number(a.id), a);
  return m;
}

// Кодовете на артикула за Конто: свой код за Конто, иначе кодът в CRM-а
// (ТЕКСТ — водещите нули са част от кода); мярка: своя, иначе по името на
// мярката от конфигурацията, иначе подразбирането.
function kontoArticle(a, K) {
  const k6 = (a && a.accounting && a.accounting.konto6) || {};
  const nz = (v) => v !== undefined && v !== null && String(v).trim() !== '';
  return {
    type: nz(k6.article_type) ? k6.article_type : K.article_type,
    id: String(nz(k6.code) ? k6.code : (a && a.code) || ''),
    measure: String(nz(k6.measure) ? k6.measure : ((K.measure_codes || {})[a && a.measure] || K.measure)),
  };
}

// D редовете на фактура в режим lines. ДДС по ред е пропорционален на
// стойността, а разликата от закръглянето отива в последния ред — сборът е
// ТОЧНО vat_amount на документа (иначе Конто не сверява документа).
function saleLines(inv, base, vat, K, arts, deal) {
  const items = (Array.isArray(inv.items) ? inv.items : []).filter((it) => it && (it.description || Number(it.unit_price)));
  if (!items.length) return [{}];
  const values = items.map((it) => round2((Number(it.quantity) || 0) * (Number(it.unit_price) || 0)));
  const sumV = values.reduce((a, b) => a + b, 0);
  let vatLeft = vat;
  return items.map((it, i) => {
    const value = values[i];
    const lineVat = i === items.length - 1 ? round2(vatLeft) : (sumV ? round2(vat * value / sumV) : 0);
    vatLeft = round2(vatLeft - lineVat);
    const qty = Number(it.quantity) || 0;
    const a = it.article_id != null ? arts.get(Number(it.article_id)) : null;
    const ka = a ? kontoArticle(a, K) : null;
    const price = Number(it.unit_price) || 0;
    const priceVat = qty ? Math.round((lineVat / qty) * 10000) / 10000 : lineVat;
    return {
      'D.ROW_NO': i + 1, 'D.DEAL_TYPE': deal,
      'D.ARTICLE_TYPE': ka ? ka.type : K.article_type,
      'D.ARTICLE_ID': ka ? ka.id : K.article_id,
      'A.ARTICLE_NAME': a ? (a.name || '') : K.article_name,
      'D.MEASURE': ka ? ka.measure : K.measure,
      'D.QTY': ka ? qty : 1,
      'D.PRICE_LOCAL': ka ? price : value, 'D.PRICE_FOREIGN': ka ? price : value,
      'D.PRICE_VAT_LOCAL': ka ? priceVat : lineVat, 'D.PRICE_VAT_FOREIGN': ka ? priceVat : lineVat,
      'D.VALUE_LOCAL': value, 'D.VALUE_FOREIGN': value,
      'D.VAT_LOCAL': lineVat, 'D.VAT_FOREIGN': lineVat,
      'D.DESCRIPTION': ka ? '' : String(it.description || '').slice(0, 200),
    };
  });
}

// ---- Артикули → „Импорт на номенклатури от Excel" (ПРОМТ-11) ----
//
// Същите колони като справката от Конто, от която кантората внася
// номенклатурата (ПРОМТ-10в, test/articles-screen: „Аналитичен показател,
// Код, Наименование, Мярка, Наименование Мярка") — кръгът се затваря: каквото
// излиза оттук, Конто го внася обратно.
const ARTICLE_HEAD = ['Аналитичен показател', 'Код', 'Наименование', 'Мярка', 'Наименование Мярка'];
function buildKonto6ArticleRows(articles, groups, company) {
  const K = kontoConfig.forCompany(company);
  const gById = new Map((Array.isArray(groups) ? groups : []).map((g) => [Number(g.id), g]));
  const byType = K.accounts_by_type || {};
  const rows = [];
  for (const a of (Array.isArray(articles) ? articles : [])) {
    const g = a.group_id != null ? gById.get(Number(a.group_id)) : null;
    const acc = (a.stock_account && String(a.stock_account)) || (g && g.stock_account && String(g.stock_account)) || ((byType[a.type] || {}).stock_account || '');
    if (!acc) continue; // услуга/ястие — не са номенклатура на склада
    const ka = kontoArticle(a, K);
    rows.push([String(acc), ka.id, a.name || '', ka.measure, a.measure || '']);
  }
  return { aoa: [ARTICLE_HEAD].concat(rows), count: rows.length };
}

module.exports = { buildKonto6Rows, buildKonto6ArticleRows, kontoArticle, ARTICLE_HEAD, LABELS, COLS };
